import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowLeft, Building2, Users, CalendarDays, Bell, 
  MessageSquare, BarChart3, Settings, CreditCard, CheckCircle,
  Eye, RefreshCw, Clock, UserPlus, Shield
} from 'lucide-react';
import BrandLogo from '../../components/BrandLogo';

export default function AdminGuide() {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/employer">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Button>
            </Link>
            <h1 className="text-xl font-bold text-slate-900">School Admin Guide</h1>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Guide Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <BrandLogo variant="black" size="xl" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">School Administrator User Guide</h1>
          <p className="text-slate-600">Complete guide to managing substitute teachers</p>
        </div>

        {/* Introduction */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-emerald-600" />
              Welcome to ExpressSub
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-600 mb-4">
              ExpressSub helps you efficiently manage substitute teacher positions at your school. 
              Post positions, track responses, and fill vacancies quickly with our streamlined platform.
            </p>
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary">Position Management</Badge>
              <Badge variant="secondary">Sub Tracking</Badge>
              <Badge variant="secondary">Real-time Notifications</Badge>
              <Badge variant="secondary">Analytics</Badge>
            </div>
          </CardContent>
        </Card>

        {/* Feature Sections */}
        <div className="space-y-6">
          {/* School Setup */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Settings className="h-5 w-5 text-blue-600" />
                1. School Setup
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-slate-600">Configure your school's basic information and default settings.</p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>School Code:</strong> Your unique code that Subs use to connect. Share this with your trusted substitutes.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Default Times:</strong> Set default start and end times for positions (pre-fills when creating new positions).</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Contact Info:</strong> Add contact person, email, and phone for Subs to reach you.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Position Categories:</strong> create categories of positions so you can easily label them when you post.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Sub Teacher Categories:</strong> create categories of teacher competencies to help in choosing the right person for the job.</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Managing Subs */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Users className="h-5 w-5 text-purple-600" />
                2. Managing Your Sub List
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-slate-600">Build and manage your pool of trusted substitute teachers.</p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Approve Requests:</strong> When a Sub enters your school code, you'll see their request on your dashboard. Approve or decline.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Categorize Subs:</strong> using the sub categories you created, classify your subs according to their areas of suitability.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Private Notes:</strong> Add notes to each Sub's profile (only you can see these).</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Remove Subs:</strong> Remove a Sub from your list entirely if needed.</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Posting Positions */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <CalendarDays className="h-5 w-5 text-orange-600" />
                3. Posting Positions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-slate-600">Create and manage substitute teacher positions.</p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Multi-Date Selection:</strong> Select any combination of dates (consecutive or non-consecutive). The Sub must accept all dates or none.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Target Specific Subs:</strong> Choose to notify all Subs or select specific ones for the position.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Category & Times:</strong> Specify the position category and exact start/end times.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Description:</strong> Add any additional details Subs should know.</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Tracking Responses */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Eye className="h-5 w-5 text-cyan-600" />
                4. Tracking Responses
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-slate-600">Monitor who has seen and responded to your positions.</p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>View Tracking:</strong> See "X of Y viewed" to know how many Subs have seen the offer.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Response Status:</strong> Track accepted, declined, and pending responses in real-time.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Auto-Reminders:</strong> set the frequency and expiration of reminders to Subs who haven't responded.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Manual Reminders:</strong> Click "Send Reminders" to immediately notify pending Subs.</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Messaging */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <MessageSquare className="h-5 w-5 text-green-600" />
                5. Messaging
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-slate-600">Communicate directly with your Subs through the app.</p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Direct Messages:</strong> Send text messages to any Sub on your list.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Message History:</strong> View your conversation history with each Sub.</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Notifications */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Bell className="h-5 w-5 text-amber-600" />
                6. Notifications
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-slate-600">Stay informed about important events.</p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Sub Requests:</strong> Get notified when a Sub requests to join your list.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Position Filled:</strong> Instant notification when a Sub accepts a position.</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Analytics */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <BarChart3 className="h-5 w-5 text-indigo-600" />
                7. Analytics
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-slate-600">Track Sub performance and reliability.</p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Acceptance Rates:</strong> See each Sub's acceptance rate for offered positions.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Position Stats:</strong> View total positions offered, accepted, and declined per Sub.</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Subscription */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <CreditCard className="h-5 w-5 text-rose-600" />
                8. Subscription
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-slate-600">
                ExpressSub offers four different pricing levels to suit individual school needs — from a free Head Start plan to a Collegiate plan for associations of schools. Discounts are available for annual billing.
              </p>
              <p className="text-sm">
                <Link
                  to="/pricing"
                  className="inline-flex items-center gap-1 text-emerald-600 hover:text-emerald-700 font-medium underline"
                  data-testid="admin-guide-pricing-link"
                >
                  See all plans on our Pricing page →
                </Link>
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-slate-200 text-center text-sm text-slate-500">
          <p>© 2026 ExpressSub. All rights reserved.</p>
          <p className="mt-1">Need help? Contact support@expresssub.com</p>
        </div>
      </main>
    </div>
  );
}
